import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  commitBulkJob,
  createBulkJob,
  downloadBulkSample,
  downloadFailedReport,
  getBulkJobHeaders,
  getBulkJobProgress,
  listBulkJobs,
  previewBulkJob,
  publishPartialBulkJob,
  retryBulkJob,
  saveBulkJobMapping,
  saveBulkJobProjectOverrides,
  selectBulkJobSheets,
} from "../../services/bulkJobService";
import { useHistory } from "react-router-dom";
import urls from "../../global/constants/UrlConstants";
import { listAssignableChannelPartners } from "../../services/channelPartnerService";
import { fetchAutomationRoutingOptions } from "../../services/organizationService";
import { loadTenantOrganizationsOnce } from "../../Shared/Organization/orgNameCache";
import { loadViewerTeamIds } from "../../helpers/assignmentScope";
import { listCallDatasets } from "../../services/callDatasetService";
import { searchProjects } from "../Project/components/stepper/StepperService";
import { getAllForms } from "../BulkUpload/components/ShowAll/ShowAllService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectMemberships,
  selectRole1,
} from "../../redux/authSlice";
import OrganizationSelect from "../../Shared/Organization/OrganizationSelect";
import SourceSubSourceFields, {
  SourceType,
} from "../../Shared/SourceSubSourceFields";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";

function normalizeOrgList(raw: any[]): { _id: string; name: string; slug?: string }[] {
  const out: { _id: string; name: string; slug?: string }[] = [];
  const seen = new Set<string>();
  (raw || []).forEach((o: any) => {
    const id = String(o?._id || o?.id || "").trim();
    if (!id || id === "undefined" || id === "all" || id === "*" || seen.has(id)) {
      return;
    }
    seen.add(id);
    out.push({
      _id: id,
      name:
        o?.name ||
        o?.brandName ||
        o?.legalName ||
        o?.slug ||
        id,
      slug: o?.slug || "",
    });
  });
  return out;
}

type JobType = "leads" | "channel_partners" | "call_dataset";
type Step = 1 | 2 | 3 | 4 | 5;

type Props = {
  defaultType?: JobType;
};

const BulkUploadWizard: React.FC<Props> = ({ defaultType = "leads" }) => {
  const history = useHistory();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const memberships = useSelector(selectMemberships);
  const hierarchyLevel = useSelector(selectRole1);
  const canSelectOrganization =
    isSuperAdmin || String(hierarchyLevel) === "1";
  const lockedOrgId =
    !canSelectOrganization &&
    activeOrgId &&
    activeOrgId !== "all" &&
    activeOrgId !== "*"
      ? String(activeOrgId)
      : "";

  const [type, setType] = useState<JobType>(defaultType);
  const [step, setStep] = useState<Step>(1);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [organizationIds, setOrganizationIds] = useState<string[]>(
    lockedOrgId
      ? [lockedOrgId]
      : activeOrgId && activeOrgId !== "all" && activeOrgId !== "*"
      ? [String(activeOrgId)]
      : []
  );
  const [projectId, setProjectId] = useState("");
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const projectSearchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [formId, setFormId] = useState("");
  const [assignMode, setAssignMode] = useState<"project" | "custom">("project");
  const [assignDepartmentIds, setAssignDepartmentIds] = useState<string[]>([]);
  const [assignTeamIds, setAssignTeamIds] = useState<string[]>([]);
  const [assignUserIds, setAssignUserIds] = useState<string[]>([]);
  const [viewerTeamIds, setViewerTeamIds] = useState<string[] | null>(null);
  const [assignOptionsLoading, setAssignOptionsLoading] = useState(false);
  const [assignOptions, setAssignOptions] = useState<{
    departments: { _id: string; name: string }[];
    teams: {
      teamId: string;
      teamName: string;
      departmentId?: string;
      departmentName?: string;
    }[];
    members: { userId: string; name: string; teamIds: string[] }[];
  }>({ departments: [], teams: [], members: [] });
  const [forms, setForms] = useState<any[]>([]);
  const [sourceType, setSourceType] = useState<SourceType>("bulk");
  const [subSource, setSubSource] = useState("");
  const [setName, setSetName] = useState("");
  const [setNameMode, setSetNameMode] = useState<"existing" | "new">("new");
  const [phoneMaskingEnabled, setPhoneMaskingEnabled] = useState<
    boolean | null
  >(null);
  const [isFromChannelPartner, setIsFromChannelPartner] = useState(false);
  const [defaultChannelPartnerId, setDefaultChannelPartnerId] = useState("");
  const [cpOptions, setCpOptions] = useState<any[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [fields, setFields] = useState<{ key: string; label: string; required?: boolean }[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [headerSamples, setHeaderSamples] = useState<Record<string, string[]>>(
    {}
  );
  const [availableSheets, setAvailableSheets] = useState<
    {
      name: string;
      headers?: string[];
      rowCount?: number;
      score?: number;
      recommended?: boolean;
    }[]
  >([]);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [preview, setPreview] = useState<any>(null);
  const [projectOverrides, setProjectOverrides] = useState<
    Record<string, string>
  >({});
  const [progress, setProgress] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [recentJobs, setRecentJobs] = useState<any[]>([]);

  useEffect(() => {
    setType(defaultType);
  }, [defaultType]);

  const loadRecentJobs = useCallback(async () => {
    try {
      // Only published datasets (final submit) — not draft preview uploads
      const res = await listBulkJobs({
        type: defaultType || type,
        limit: 100,
        organizationId: organizationIds[0] || undefined,
        status: "completed,partial,failed,processing",
      });
      const items = Array.isArray(res?.data?.items)
        ? res.data.items
        : Array.isArray(res?.items)
        ? res.items
        : [];
      setRecentJobs(items);
    } catch {
      setRecentJobs([]);
    }
  }, [defaultType, type, organizationIds]);

  useEffect(() => {
    loadRecentJobs();
  }, [loadRecentJobs, step]);

  const [scopedDatasetNames, setScopedDatasetNames] = useState<string[]>([]);

  useEffect(() => {
    if ((defaultType || type) !== "call_dataset") {
      setScopedDatasetNames([]);
      return;
    }
    const org = String(organizationIds[0] || "");
    if (!org) {
      setScopedDatasetNames([]);
      return;
    }
    listCallDatasets(org, { owned: true })
      .then((res) => {
        const names = (Array.isArray(res?.data) ? res.data : [])
          .map((row: any) => String(row?.datasetName || "").trim())
          .filter(Boolean);
        setScopedDatasetNames(names);
      })
      .catch(() => setScopedDatasetNames([]));
  }, [defaultType, type, organizationIds, step]);

  const existingSetNames = useMemo(() => {
    if ((defaultType || type) === "call_dataset") return scopedDatasetNames;
    const orgId = String(organizationIds[0] || "");
    const names = new Map<string, string>();
    for (const j of recentJobs) {
      if (orgId) {
        const jOrg = String(
          j.organizationId ||
            (Array.isArray(j.organizationIds) && j.organizationIds[0]) ||
            ""
        );
        if (jOrg && jOrg !== orgId) continue;
      }
      const name = String(j?.setName || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (!names.has(key)) names.set(key, name);
    }
    return Array.from(names.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [recentJobs, organizationIds, defaultType, type, scopedDatasetNames]);

  useEffect(() => {
    if (setNameMode !== "existing") return;
    if (!setName) return;
    const ok = existingSetNames.some(
      (n) => n.toLowerCase() === setName.trim().toLowerCase()
    );
    if (!ok) setSetName("");
  }, [existingSetNames, setNameMode, setName]);

  useEffect(() => {
    if (setNameMode === "existing" && existingSetNames.length === 0) {
      setSetNameMode("new");
    }
  }, [setNameMode, existingSetNames.length]);

  useEffect(() => {
    (async () => {
      try {
        const res = await loadTenantOrganizationsOnce();
        const raw = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.items)
          ? res.items
          : [];
        let list = normalizeOrgList(raw);
        if (!list.length && (memberships || []).length) {
          list = normalizeOrgList(
            (memberships || []).map((m: any) => ({
              _id: m?.organizationId || m?.organization?._id,
              id: m?.organizationId || m?.organization?._id,
              name:
                m?.organization?.name ||
                m?.organization?.brandName ||
                m?.organizationName,
              brandName: m?.organization?.brandName,
              legalName: m?.organization?.legalName,
              slug: m?.organization?.slug,
            }))
          );
        }
        setOrgs(list);
      } catch (_) {
        setOrgs(
          normalizeOrgList(
            (memberships || []).map((m: any) => ({
              _id: m?.organizationId || m?.organization?._id,
              name:
                m?.organization?.name ||
                m?.organization?.brandName ||
                m?.organizationName,
              slug: m?.organization?.slug,
            }))
          )
        );
      }
    })();
  }, [memberships]);

  useEffect(() => {
    if (lockedOrgId) {
      setOrganizationIds([lockedOrgId]);
      return;
    }
    if (activeOrgId && activeOrgId !== "all" && activeOrgId !== "*") {
      setOrganizationIds([String(activeOrgId)]);
    }
  }, [lockedOrgId, activeOrgId]);

  // Drop stale "all" / invalid org ids so the picker can show a real selection
  useEffect(() => {
    setOrganizationIds((prev) => {
      const next = (prev || []).filter(
        (id) => id && id !== "all" && id !== "*" && id !== "undefined"
      );
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const loadProjects = useCallback(
    async (search = "") => {
      try {
        const orgId = organizationIds[0] || "";
        let q = String(search || "").trim();
        // Common typo: "omaz" → match "Omaxe" (and similar)
        if (/^omaz/i.test(q)) q = q.replace(/^omaz/i, "omax");

        const res = await searchProjects({
          search: q,
          page: 0,
          limit: q ? 100 : 500,
          organizationId: orgId || undefined,
        });
        let list = Array.isArray(res?.data) ? res.data : [];

        // If exact search empty, broaden with first 3 chars (e.g. omaz → oma)
        if (!list.length && q.length >= 3) {
          const broad = await searchProjects({
            search: q.slice(0, 3),
            page: 0,
            limit: 100,
            organizationId: orgId || undefined,
          });
          list = Array.isArray(broad?.data) ? broad.data : [];
        }
        setProjects(list);
      } catch (_) {
        setProjects([]);
      }
    },
    [organizationIds]
  );

  useEffect(() => {
    void loadProjects("");
  }, [loadProjects]);

  const handleProjectSearch = useCallback(
    (query: string) => {
      if (projectSearchTimer.current) clearTimeout(projectSearchTimer.current);
      projectSearchTimer.current = setTimeout(() => {
        void loadProjects(query);
      }, 280);
    },
    [loadProjects]
  );

  useEffect(() => {
    if (type !== "leads") return;
    (async () => {
      try {
        const res = await getAllForms();
        const list = Array.isArray(res?.data) ? res.data : [];
        setForms(list.filter((f: any) => String(f.status) === "1" || f.status === 1 || f.status == null));
      } catch (_) {
        setForms([]);
      }
    })();
  }, [type]);

  const projectOptions = useMemo(() => {
    const orgSet = new Set(
      (organizationIds || [])
        .map(String)
        .filter((id) => id && id !== "all" && id !== "*")
    );
    return (projects || [])
      .filter((p: any) => {
        if (!orgSet.size) return true;
        const oid = String(p.organizationId || "");
        // Master list may omit org — still show; when present, match selected org(s)
        if (!oid) return true;
        return orgSet.has(oid);
      })
      .map((p: any) => ({
        label:
          p.project_name ||
          p.name ||
          p.projects?.project_name ||
          p.project_uid ||
          String(p._id),
        value: String(p._id || p.projectId || ""),
      }))
      .filter((o) => o.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [projects, organizationIds]);

  const formOptions = useMemo(() => {
    const orgSet = new Set(
      (organizationIds || []).map(String).filter(Boolean)
    );
    return (forms || [])
      .filter((f: any) => {
        if (projectId && String(f.projectId || "") !== String(projectId)) {
          return false;
        }
        if (orgSet.size) {
          const oid = String(f.organizationId || "");
          if (oid && !orgSet.has(oid)) return false;
        }
        return true;
      })
      .map((f: any) => ({
        label: f.form_name || f.name || String(f._id),
        value: String(f._id),
      }));
  }, [forms, projectId, organizationIds]);

  useEffect(() => {
    // Clear form if it no longer belongs to selected project/org
    if (!formId) return;
    if (!formOptions.some((o) => o.value === formId)) {
      setFormId("");
    }
  }, [formOptions, formId]);

  useEffect(() => {
    const orgId = organizationIds[0];
    if (!orgId || !isFromChannelPartner) {
      setCpOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await listAssignableChannelPartners(orgId, projectId || undefined);
        const list = res?.data || res || [];
        setCpOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        setCpOptions([]);
      }
    })();
  }, [organizationIds, projectId, isFromChannelPartner]);

  useEffect(() => {
    const orgId = organizationIds[0];
    if (!orgId || type !== "leads" || assignMode !== "custom") return;
    let cancelled = false;
    setAssignOptionsLoading(true);
    (async () => {
      try {
        const [scope, res] = await Promise.all([
          loadViewerTeamIds(),
          fetchAutomationRoutingOptions(orgId, { lite: true }),
        ]);
        if (cancelled) return;
        const data = res?.data || {};
        setViewerTeamIds(scope);
        const allow =
          scope == null ? null : new Set(scope.map(String));
        const teams = (Array.isArray(data.teams) ? data.teams : []).filter(
          (t: any) => allow == null || allow.has(String(t.teamId))
        );
        const deptIds = new Set(
          teams.map((t: any) => String(t.departmentId || "")).filter(Boolean)
        );
        setAssignOptions({
          departments: (Array.isArray(data.departments) ? data.departments : [])
            .filter((d: any) => allow == null || deptIds.has(String(d._id))),
          teams,
          members: (Array.isArray(data.members) ? data.members : []).filter(
            (m: any) =>
              allow == null ||
              (m.teamIds || []).some((tid: string) => allow.has(String(tid)))
          ),
        });
      } catch {
        if (!cancelled) {
          setAssignOptions({ departments: [], teams: [], members: [] });
        }
      } finally {
        if (!cancelled) setAssignOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationIds, type, assignMode]);

  useEffect(() => {
    const orgId = organizationIds[0];
    if (!orgId || type !== "leads" || assignMode !== "custom") return;
    if (!assignTeamIds.length) {
      setAssignOptions((prev) =>
        prev.members.length ? { ...prev, members: [] } : prev
      );
      setAssignUserIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchAutomationRoutingOptions(orgId, {
          lite: true,
          teamIds: assignTeamIds,
        });
        if (cancelled) return;
        const allow =
          viewerTeamIds == null ? null : new Set(viewerTeamIds.map(String));
        const members = (Array.isArray(res?.data?.members) ? res.data.members : [])
          .filter(
            (m: any) =>
              allow == null ||
              (m.teamIds || []).some((tid: string) => allow.has(String(tid)))
          )
          .map((m: any) => ({
            userId: String(m.userId),
            name: m.name || "User",
            teamIds: (m.teamIds || []).map(String),
          }));
        setAssignOptions((prev) => ({ ...prev, members }));
        setAssignUserIds((prev) =>
          prev.filter((id) => members.some((m: { userId: string }) => m.userId === id))
        );
      } catch {
        if (!cancelled) {
          setAssignOptions((prev) => ({ ...prev, members: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationIds, type, assignMode, assignTeamIds, viewerTeamIds]);

  const departmentOptions = useMemo(
    () =>
      assignOptions.departments.map((d) => ({
        label: d.name,
        value: d._id,
      })),
    [assignOptions.departments]
  );
  const teamOptions = useMemo(
    () =>
      assignOptions.teams
        .filter(
          (t) =>
            !assignDepartmentIds.length ||
            assignDepartmentIds.includes(String(t.departmentId || ""))
        )
        .map((t) => ({
          label: t.departmentName
            ? `${t.teamName} (${t.departmentName})`
            : t.teamName,
          value: t.teamId,
        })),
    [assignOptions.teams, assignDepartmentIds]
  );
  const memberOptions = useMemo(
    () =>
      assignOptions.members.map((m) => ({
        label: m.name,
        value: m.userId,
      })),
    [assignOptions.members]
  );

  const orgOptions = useMemo(
    () =>
      orgs
        .map((o) => {
          const id = String(o._id || o.id || "").trim();
          if (!id) return null;
          return {
            label:
              o.name ||
              o.brandName ||
              o.legalName ||
              o.slug ||
              id,
            value: id,
          };
        })
        .filter(Boolean) as { label: string; value: string }[],
    [orgs]
  );




  // Keep project in sync when org changes
  useEffect(() => {
    if (type !== "leads" || !projectId) return;
    if (!projectOptions.some((o) => o.value === projectId)) {
      setProjectId("");
    }
  }, [type, projectId, projectOptions]);

  const autoMap = useCallback((hdrs: string[], flds: { key: string; label: string }[]) => {
    const next: Record<string, string> = {};
    const used = new Set<string>();
    const norm = (s: string) =>
      String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const score = (nh: string, f: { key: string; label: string }) => {
      const nk = norm(f.key);
      const nl = norm(f.label);
      if (!nh) return 0;
      if (nh === nk || nh === nl) return 100;
      if (
        (nh.includes("country") || nh.includes("dial")) &&
        (nk.includes("country") || nk.includes("dial"))
      )
        return 96;
      if (nh.includes("phone") && (nk === "phone" || nl.includes("phone"))) return 95;
      if (nh.includes("email") && (nk === "email" || nl.includes("email"))) return 95;
      if (
        (nh.includes("firm") || nh.includes("company") || nh.includes("partner")) &&
        (nk === "firmname" || nl.includes("firm"))
      )
        return 90;
      if (nh.includes("grade") && nk === "grade") return 90;
      if (nh.includes("count") && nk.includes("count")) return 85;
      if (
        (nh.includes("stage") || nh === "status") &&
        (nk.includes("stage") || nk === "status")
      )
        return 94;
      if (nh.includes("date") && nk.includes("date")) return 92;
      if (
        nh.includes("project") &&
        (nk === "projectname" || nk === "projectid" || nl.includes("project"))
      )
        return 97;
      if (nh.includes(nk) && nk.length >= 4) return 50;
      if (nl.includes(nh) && nh.length >= 4) return 40;
      return 0;
    };
    for (const h of hdrs) {
      const nh = norm(h);
      let best: { key: string; s: number } | null = null;
      for (const f of flds) {
        if (used.has(f.key)) continue;
        const s = score(nh, f);
        if (s > 0 && (!best || s > best.s)) best = { key: f.key, s };
      }
      if (best) {
        next[h] = best.key;
        used.add(best.key);
      }
    }
    return next;
  }, []);

  const validateStep1 = () => {
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return false;
    }
    if (!setName.trim()) {
      openErrorNotification(
        setNameMode === "existing"
          ? "Select an existing dataset"
          : "Dataset name is required"
      );
      return false;
    }
    if (setNameMode === "new") {
      const clash = existingSetNames.some(
        (n) => n.toLowerCase() === setName.trim().toLowerCase()
      );
      if (clash) {
        openErrorNotification(
          "That dataset name already exists. Select it under Existing, or choose a new unique name."
        );
        return false;
      }
    }
    if (type === "channel_partners" && phoneMaskingEnabled === null) {
      openErrorNotification("Choose whether phone masking is enabled for these CPs");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!file) {
      openErrorNotification("Choose an .xlsx or .csv file");
      return;
    }
    const name = String(file.name || "").toLowerCase();
    if (name.endsWith(".numbers")) {
      openErrorNotification(
        "Apple Numbers (.numbers) is not supported. In Numbers: File → Export To → Excel (.xlsx), then upload that file."
      );
      return;
    }
    if (!/\.(xlsx|csv)$/i.test(name)) {
      openErrorNotification("Only .xlsx or .csv files are supported");
      return;
    }
    if (!validateStep1()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      fd.append("organizationIds", JSON.stringify(organizationIds));
      fd.append("organizationId", organizationIds[0]);
      fd.append("sourceType", sourceType);
      fd.append("subSource", subSource.trim());
      // Draft only — dataset is created on final Save unique (commit)
      if (setName.trim()) {
        fd.append("pendingSetName", setName.trim().slice(0, 120));
        fd.append("setName", setName.trim().slice(0, 120));
      }
      if (setNameMode === "existing") {
        fd.append("reuseExistingSet", "true");
      }
      if (type === "leads") {
        if (formId.trim()) fd.append("formId", formId.trim());
        fd.append("isFromChannelPartner", String(isFromChannelPartner));
        if (defaultChannelPartnerId) {
          fd.append("defaultChannelPartnerId", defaultChannelPartnerId);
        }
        fd.append("assignMode", assignMode);
        if (assignMode === "custom") {
          const teamsForUpload = assignTeamIds.length
            ? assignTeamIds
            : assignOptions.teams
                .filter(
                  (t) =>
                    !assignDepartmentIds.length ||
                    assignDepartmentIds.includes(String(t.departmentId || ""))
                )
                .map((t) => t.teamId);
          fd.append("assignTeamIds", JSON.stringify(teamsForUpload));
          fd.append("assignUserIds", JSON.stringify(assignUserIds));
        }
      } else {
        fd.append("phoneMaskingEnabled", String(!!phoneMaskingEnabled));
        if (projectIds.length) {
          fd.append("projectIds", JSON.stringify(projectIds));
        }
      }
      const res = await createBulkJob(fd);
      const job = res?.data || res;
      setJobId(String(job._id));
      const hdr = await getBulkJobHeaders(String(job._id));
      const data = hdr?.data || hdr;
      const sheets = data.sheets || job.availableSheets || [];
      const selected =
        data.selectedSheets ||
        job.sheetNames ||
        (job.sheetName ? [job.sheetName] : []);
      setAvailableSheets(sheets);
      setSelectedSheets(selected);
      setHeaders(data.headers || job.headers || []);
      setHeaderSamples(data.headerSamples || {});
      setFields(data.fields || []);
      setMapping(autoMap(data.headers || job.headers || [], data.fields || []));
      setProjectOverrides({});
      setPreview(null);
      setStep(3);
      openSuccessNotification(
        sheets.length > 1
          ? `Uploaded — ${sheets.length} sheets found. Select sheet(s) then map columns.`
          : "File uploaded — map columns next"
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleSheet = (name: string) => {
    setSelectedSheets((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const applySheetSelection = async () => {
    if (!selectedSheets.length) {
      openErrorNotification("Select at least one sheet");
      return;
    }
    setBusy(true);
    try {
      const res = await selectBulkJobSheets(jobId, selectedSheets);
      const data = res?.data || res;
      const nextHeaders = data.headers || [];
      setHeaders(nextHeaders);
      setSelectedSheets(data.selectedSheets || selectedSheets);
      setMapping(autoMap(nextHeaders, fields));
      try {
        const hdr = await getBulkJobHeaders(jobId);
        const hdrData = hdr?.data || hdr;
        if (hdrData?.headerSamples) setHeaderSamples(hdrData.headerSamples);
        if (hdrData?.headers?.length) setHeaders(hdrData.headers);
      } catch (_) {
        /* ignore sample refresh */
      }
      openSuccessNotification(
        `Using ${
          (data.selectedSheets || selectedSheets).length
        } sheet(s) — map columns below`
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not apply sheet selection");
    } finally {
      setBusy(false);
    }
  };

  const unmappedRequired = useMemo(() => {
    const required = fields.filter((f) => f.required).map((f) => f.key);
    const mappedFields = new Set(Object.values(mapping).filter(Boolean));
    return required.filter((r) => !mappedFields.has(r));
  }, [fields, mapping]);

  const mappingSummary = useMemo(() => {
    return headers.map((h) => {
      const fieldKey = mapping[h] || "";
      const field = fields.find((f) => f.key === fieldKey);
      const samples = Array.isArray(headerSamples?.[h])
        ? headerSamples[h]
        : [];
      return {
        header: h,
        fieldKey,
        fieldLabel: field?.label || "",
        required: !!field?.required,
        samples,
        status: fieldKey ? "mapped" : "unmapped",
      };
    });
  }, [headers, mapping, fields, headerSamples]);

  const unresolvedProjects = useMemo(() => {
    const list = preview?.projectResolution?.unresolved;
    return Array.isArray(list) ? list : [];
  }, [preview]);

  const unresolvedNeedsMap = useMemo(() => {
    return unresolvedProjects.filter((u: any) => {
      const tok = String(u?.token || u || "").trim();
      return tok && !String(projectOverrides[tok] || "").trim();
    });
  }, [unresolvedProjects, projectOverrides]);

  const handleSaveMapping = async () => {
    if (!selectedSheets.length && availableSheets.length > 1) {
      openErrorNotification("Select at least one sheet first");
      return;
    }
    if (unmappedRequired.length) {
      const labels = unmappedRequired
        .map((k) => fields.find((f) => f.key === k)?.label || k)
        .join(", ");
      openErrorNotification(
        `Map required field(s) before preview: ${labels}`
      );
      return;
    }
    setBusy(true);
    try {
      if (availableSheets.length > 1) {
        await selectBulkJobSheets(jobId, selectedSheets);
      }
      await saveBulkJobMapping(jobId, mapping);
      const res = await previewBulkJob(jobId);
      const data = res?.data || res;
      setPreview(data);
      setProjectOverrides(data?.projectTokenOverrides || {});
      if (data?.headerSamples) setHeaderSamples(data.headerSamples);
      setStep(4);
    } catch (e: any) {
      openErrorNotification(e?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const pollProgress = useCallback(
    async (id: string) => {
      const tick = async () => {
        try {
          const res = await getBulkJobProgress(id);
          const data = res?.data || res;
          setProgress(data);
          if (
            data?.status === "processing" ||
            data?.status === "previewed" ||
            data?.status === "mapped"
          ) {
            if (data?.status === "processing") {
              setTimeout(tick, 1500);
            }
          }
          if (["completed", "partial", "failed"].includes(data?.status)) {
            setProgress(data);
          }
        } catch (_) {
          setTimeout(tick, 2000);
        }
      };
      tick();
    },
    []
  );

  const handleCommit = async (
    mode: "unique_only" | "all" | "discard" | "unique_and_membership_update"
  ) => {
    setBusy(true);
    try {
      if (mode === "discard") {
        await commitBulkJob(jobId, "discard");
        openSuccessNotification("Upload discarded");
        setStep(1);
        setJobId("");
        setPreview(null);
        setFile(null);
        return;
      }
      if (!setName.trim()) {
        openErrorNotification(
          setNameMode === "existing"
            ? "Select an existing dataset before saving"
            : "Dataset name is required before saving"
        );
        return;
      }
      if (setNameMode === "new") {
        const clash = existingSetNames.some(
          (n) => n.toLowerCase() === setName.trim().toLowerCase()
        );
        if (clash) {
          openErrorNotification(
            "That dataset name already exists. Select it under Existing, or choose a new unique name."
          );
          return;
        }
      }
      if (
        (type === "leads" || type === "call_dataset") &&
        unresolvedNeedsMap.length
      ) {
        openErrorNotification(
          `Manually map ${unresolvedNeedsMap.length} unmatched project name(s) before upload`
        );
        return;
      }
      if (
        (type === "leads" || type === "call_dataset") &&
        Object.keys(projectOverrides).length
      ) {
        await saveBulkJobProjectOverrides(jobId, projectOverrides);
      }
      await commitBulkJob(jobId, mode, {
        setName: setName.trim().slice(0, 120),
        reuseExistingSet: setNameMode === "existing",
        projectTokenOverrides: projectOverrides,
      });
      setStep(5);
      pollProgress(jobId);
      openSuccessNotification("Processing started — dataset created");
      loadRecentJobs();
    } catch (e: any) {
      openErrorNotification(e?.message || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const percent = Number(progress?.progress?.percent || 0);

  const orgLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orgs) {
      const id = String(o._id || o.id || "");
      if (!id) continue;
      map.set(
        id,
        o.name || o.brandName || o.legalName || o.slug || id
      );
    }
    for (const opt of orgOptions) {
      if (!map.has(opt.value)) map.set(opt.value, opt.label);
    }
    return map;
  }, [orgs, orgOptions]);

  return (
    <div className="box-border w-full space-y-5 px-4 py-4 md:px-6 md:py-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-[#111827]">
            {type === "channel_partners"
              ? "Partner Bulk Upload"
              : type === "call_dataset"
                ? "Call Dataset Upload"
                : "Leads Bulk Upload"}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-[#6B7280]">
            {type === "call_dataset"
              ? "Upload into a call dataset. Rows stay here for IVR and stage updates until you convert them into leads."
              : "Download the dummy sample for column format, select an existing dataset or create a new one, upload your file, map columns, then save."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() =>
              downloadBulkSample(type, "xlsx").catch((e) =>
                openErrorNotification(e.message)
              )
            }
          >
            Sample .xlsx
          </YhButton>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() =>
              downloadBulkSample(type, "csv").catch((e) =>
                openErrorNotification(e.message)
              )
            }
          >
            Sample .csv
          </YhButton>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {[
          [1, "Context"],
          [2, "Upload"],
          [3, "Map columns"],
          [4, "Preview"],
          [5, "Progress"],
        ].map(([n, label]) => (
          <span
            key={String(n)}
            className={`rounded-full px-3 py-1 ${
              step === n
                ? "bg-[#111827] text-white"
                : step > Number(n)
                ? "bg-[#D1FAE5] text-[#065F46]"
                : "bg-[#F3F4F6] text-[#6B7280]"
            }`}
          >
            {n}. {label}
          </span>
        ))}
      </div>

      {step <= 2 && (
        <div className="space-y-4 rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">
              New upload set
            </h2>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              Put the <strong>project name</strong> from Projects in the file
              (exact name). One name such as{" "}
              <code className="rounded bg-[#F3F4F6] px-1">Skyline Residency</code>
              , or several separated by a comma.{" "}
              <code className="rounded bg-[#F3F4F6] px-1">channel_partner_firm</code>{" "}
              is optional in the sample. Unmatched names must be mapped manually
              in Preview before upload.
            </p>
          </div>
          <div className="space-y-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3">
            <p className="text-sm font-medium text-[#111827]">
              Dataset name *
            </p>
            <p className="text-xs text-[#6B7280]">
              {type === "call_dataset"
                ? "Choose a dataset you can see. A team lead only sees datasets assigned to their team. An admin sees every dataset."
                : "Choose a name now; the dataset is created only when you click Save unique at the end. Abandoned previews do not create a set."}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  setNameMode === "existing"
                    ? "bg-[#111827] text-white"
                    : "bg-white text-[#374151] ring-1 ring-[#E5E7EB]"
                }`}
                disabled={!existingSetNames.length}
                onClick={() => {
                  setSetNameMode("existing");
                  setSetName("");
                }}
              >
                Select existing
              </button>
              <button
                type="button"
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  setNameMode === "new"
                    ? "bg-[#111827] text-white"
                    : "bg-white text-[#374151] ring-1 ring-[#E5E7EB]"
                }`}
                onClick={() => {
                  setSetNameMode("new");
                  setSetName("");
                }}
              >
                Create new
              </button>
            </div>
            {setNameMode === "existing" ? (
              <YhSelect
                label="Existing dataset"
                requiredMark
                searchable
                clearable
                disabled={!organizationIds[0] || !existingSetNames.length}
                placeholder={
                  !organizationIds[0]
                    ? "Select organization first…"
                    : existingSetNames.length
                    ? "Select dataset…"
                    : "No datasets yet — create new"
                }
                value={setName}
                onChange={(e: any) =>
                  setSetName(String(e?.target?.value || ""))
                }
                options={[
                  { label: "Select dataset…", value: "" },
                  ...existingSetNames.map((n) => ({
                    label: n,
                    value: n,
                  })),
                ]}
              />
            ) : (
              <YhInput
                label="New dataset name"
                requiredMark
                value={setName}
                onChange={(e: any) => setSetName(e.target.value)}
                placeholder="e.g. February leads"
              />
            )}
            {!organizationIds[0] ? (
              <p className="text-xs text-[#B45309]">
                Select an organization to load existing datasets.
              </p>
            ) : setNameMode === "new" ? (
              <p className="text-xs text-[#9CA3AF]">
                {type === "call_dataset"
                  ? "New call datasets stay separate from leads until someone converts a row."
                  : "New names must be unique among saved datasets for this organization."}
              </p>
            ) : (
              <p className="text-xs text-[#9CA3AF]">
                {type === "call_dataset"
                  ? "More rows are added to this dataset only. You cannot add rows to a dataset that is not assigned to you."
                  : "New uploads under this name stay grouped for WhatsApp audience filters."}
              </p>
            )}
          </div>
          {canSelectOrganization ? (
            type === "channel_partners" ? (
              <OrganizationSelect
                multiple
                label="Organization(s)"
                required
                value={organizationIds}
                onChange={(ids) =>
                  setOrganizationIds(
                    (ids || [])
                      .map(String)
                      .filter((id) => id && id !== "all" && id !== "*")
                  )
                }
              />
            ) : (
              <OrganizationSelect
                label="Organization"
                required
                allowEmpty
                emptyLabel="Select organization…"
                value={organizationIds[0] || ""}
                onChange={(id) => {
                  const v = String(id || "").trim();
                  setOrganizationIds(
                    v && v !== "all" && v !== "*" ? [v] : []
                  );
                  setProjectId("");
                  setFormId("");
                }}
              />
            )
          ) : (
            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-sm text-[#374151]">
              Organization is set from your login
              {lockedOrgId ? (
                <>
                  {" "}
                  (
                  {orgLabelById.get(lockedOrgId) || lockedOrgId}
                  ).
                </>
              ) : (
                "."
              )}
            </div>
          )}
          {canSelectOrganization && organizationIds[0] ? (
            <p className="text-xs text-[#6B7280]">
              Selected organization:{" "}
              <span className="font-semibold text-[#111827]">
                {orgLabelById.get(String(organizationIds[0])) ||
                  organizationIds[0]}
              </span>
              {activeOrgId === "all" || activeOrgId === "*" ? (
                <span className="text-[#9CA3AF]">
                  {" "}
                  (header is All Organizations — pick a specific org for this
                  upload)
                </span>
              ) : null}
            </p>
          ) : null}

          <SourceSubSourceFields
            organizationId={organizationIds[0] || ""}
            sourceType={sourceType}
            subSource={subSource}
            onSourceTypeChange={setSourceType}
            onSubSourceChange={setSubSource}
            required={false}
            applyToAllHint={type !== "call_dataset"}
          />
          {type === "call_dataset" ? (
            <p className="-mt-2 text-xs text-[#6B7280]">
              Optional. Stored on each dataset row, not as a lead.
            </p>
          ) : null}

          {type === "leads" ? (
            <>
              <YhSelect
                label="Form (optional — for dynamic field mapping)"
                value={formId}
                onChange={(e: any) =>
                  setFormId(String(e?.target?.value || ""))
                }
                options={[
                  {
                    label: "No form / skip dynamic fields",
                    value: "",
                  },
                  ...formOptions,
                ]}
              />
              {!organizationIds.length ? (
                <p className="text-xs text-[#9CA3AF]">
                  Select an organization to load its projects.
                </p>
              ) : formOptions.length === 0 ? (
                <p className="text-xs text-[#9CA3AF]">
                  No active forms found (optional).
                </p>
              ) : null}
              <div className="space-y-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                <p className="text-sm font-medium text-[#111827]">
                  Bulk assign
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="assignMode"
                    checked={assignMode === "project"}
                    onChange={() => setAssignMode("project")}
                  />
                  Project members (default) — each lead goes to the people saved on its project
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="assignMode"
                    checked={assignMode === "custom"}
                    onChange={() => setAssignMode("custom")}
                  />
                  Choose department, team, or member
                </label>
                {assignMode === "custom" ? (
                  <>
                    <YhMultiSelect
                      label="Department"
                      placeholder={
                        assignOptionsLoading
                          ? "Loading departments..."
                          : "Select departments..."
                      }
                      searchPlaceholder="Search departments..."
                      showSelectAll
                      maxVisibleChips={3}
                      disabled={assignOptionsLoading}
                      options={departmentOptions}
                      value={assignDepartmentIds}
                      onChange={(values) => {
                        const next = values.map(String);
                        setAssignDepartmentIds(next);
                        setAssignTeamIds((prev) =>
                          prev.filter((id) =>
                            assignOptions.teams.some(
                              (t) =>
                                t.teamId === id &&
                                (!next.length ||
                                  next.includes(String(t.departmentId || "")))
                            )
                          )
                        );
                      }}
                    />
                    <YhMultiSelect
                      label="Team"
                      placeholder={
                        assignOptionsLoading
                          ? "Loading teams..."
                          : viewerTeamIds && viewerTeamIds.length === 0
                            ? "No team on your login"
                            : "Select teams..."
                      }
                      searchPlaceholder="Search teams..."
                      showSelectAll
                      maxVisibleChips={3}
                      disabled={assignOptionsLoading}
                      options={teamOptions}
                      value={assignTeamIds}
                      onChange={(values) => setAssignTeamIds(values.map(String))}
                    />
                    <YhMultiSelect
                      label="Member"
                      placeholder={
                        assignTeamIds.length
                          ? "Select members..."
                          : "Select a team first"
                      }
                      searchPlaceholder="Search members..."
                      showSelectAll
                      maxVisibleChips={3}
                      disabled={!assignTeamIds.length}
                      options={memberOptions}
                      value={assignUserIds}
                      onChange={(values) => setAssignUserIds(values.map(String))}
                    />
                    {viewerTeamIds != null ? (
                      <p className="text-xs text-[#6B7280]">
                        Your login can assign only your team and its members.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-xs text-[#6B7280]">
                    Uploaded leads are assigned only to the members saved on
                    each project. A project with no members is left for review.
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isFromChannelPartner}
                  onChange={(e) => setIsFromChannelPartner(e.target.checked)}
                />
                Leads are from a Channel Partner
              </label>
              {isFromChannelPartner && (
                <YhSelect
                  label="Default Channel Partner"
                  value={defaultChannelPartnerId}
                  onChange={(e: any) =>
                    setDefaultChannelPartnerId(String(e?.target?.value || ""))
                  }
                  options={[
                    { label: "Select CP…", value: "" },
                    ...cpOptions.map((c) => ({
                      label: `${c.firmName} (${c.phone})`,
                      value: String(c._id),
                    })),
                  ]}
                />
              )}
            </>
          ) : (
            <>
              <YhMultiSelect
                label="Projects (optional)"
                placeholder="Select all or particular projects…"
                searchPlaceholder="Search projects…"
                showSelectAll
                maxVisibleChips={4}
                options={projectOptions}
                value={projectIds}
                onChange={(values) => setProjectIds(values.map(String))}
                onSearchChange={handleProjectSearch}
              />
              <p className="text-xs text-[#9CA3AF]">
                If you select project(s), those projects are assigned to each
                uploaded Channel Partner. Leave empty to link them to all
                projects in the selected organization(s).
              </p>
              <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                <p className="mb-2 text-sm font-medium text-[#111827]">
                  Enable phone masking for these Channel Partners? *
                </p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="masking"
                      checked={phoneMaskingEnabled === true}
                      onChange={() => setPhoneMaskingEnabled(true)}
                    />
                    Yes — mask phones
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="masking"
                      checked={phoneMaskingEnabled === false}
                      onChange={() => setPhoneMaskingEnabled(false)}
                    />
                    No — do not mask
                  </label>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-[#374151]">
              Upload file (.xlsx / .csv) *
            </label>
            <input
              type="file"
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <p className="mt-1 text-xs text-[#6B7280]">{file.name}</p>
            ) : null}
            <p className="mt-1 text-xs text-[#9CA3AF]">
              Supported: <strong>.xlsx</strong> and <strong>.csv</strong> (comma,
              semicolon, or tab). Not Apple Numbers (.numbers) or old .xls —
              export to .xlsx/.csv first. Samples are format guides only — upload
              your real data file.
            </p>
          </div>

          <YhButton
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={handleUpload}
          >
            {busy ? "Uploading…" : "Continue to column mapping"}
          </YhButton>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4 rounded-xl border border-[#E5E7EB] bg-white p-4">
          {availableSheets.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-base font-semibold">
                Select sheet(s) to import
              </h2>
              <p className="text-sm text-[#6B7280]">
                This file has {availableSheets.length} sheet
                {availableSheets.length > 1 ? "s" : ""}. Choose one or more —
                preview and save will combine selected sheets. Sheets with a
                Phone column are recommended.
              </p>
              <div className="space-y-2">
                {availableSheets.map((s) => {
                  const checked = selectedSheets.includes(s.name);
                  return (
                    <label
                      key={s.name}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 ${
                        checked
                          ? "border-[#111827] bg-[#F9FAFB]"
                          : "border-[#E5E7EB]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        onChange={() => toggleSheet(s.name)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-[#111827]">
                          {s.name}
                          {s.recommended ? (
                            <span className="rounded bg-[#D1FAE5] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#065F46]">
                              Recommended
                            </span>
                          ) : null}
                          <span className="text-xs font-normal text-[#6B7280]">
                            ~{s.rowCount ?? 0} rows
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-[#9CA3AF]">
                          Columns: {(s.headers || []).slice(0, 8).join(", ") || "—"}
                          {(s.headers || []).length > 8 ? "…" : ""}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {availableSheets.length > 1 && (
                <YhButton
                  variant="secondary"
                  size="sm"
                  disabled={busy || !selectedSheets.length}
                  onClick={applySheetSelection}
                >
                  Apply sheet selection
                </YhButton>
              )}
            </div>
          )}

          <h2 className="text-base font-semibold">Map sheet columns → fields</h2>
          <p className="text-xs text-[#6B7280]">
            Review each column’s sample values and map it to a Pulse field.
            Required fields must be mapped before you can preview.
          </p>
          {unmappedRequired.length ? (
            <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">
              Still need mapping:{" "}
              <strong>
                {unmappedRequired
                  .map((k) => fields.find((f) => f.key === k)?.label || k)
                  .join(", ")}
              </strong>
            </div>
          ) : (
            <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] px-3 py-2 text-sm text-[#065F46]">
              All required fields are mapped.
            </div>
          )}
          <div className="overflow-auto rounded-xl border border-[#E5E7EB]">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F9FAFB] text-xs uppercase tracking-wide text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Sheet column</th>
                  <th className="px-3 py-2 font-semibold">Sample values</th>
                  <th className="px-3 py-2 font-semibold">Maps to</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {mappingSummary.map((row) => (
                  <tr
                    key={row.header}
                    className="border-t border-[#F3F4F6] align-top"
                  >
                    <td className="px-3 py-2 font-medium text-[#111827]">
                      {row.header}
                    </td>
                    <td className="px-3 py-2 text-xs text-[#4B5563]">
                      {(row.samples || []).filter(Boolean).length ? (
                        <ul className="space-y-0.5">
                          {(row.samples || [])
                            .filter(Boolean)
                            .slice(0, 3)
                            .map((s, i) => (
                              <li key={i} className="truncate max-w-[240px]">
                                {s}
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="w-full min-w-[180px] rounded-lg border border-[#E5E7EB] px-2 py-1.5 text-sm"
                        value={mapping[row.header] || ""}
                        onChange={(e) =>
                          setMapping((m) => ({
                            ...m,
                            [row.header]: e.target.value,
                          }))
                        }
                      >
                        <option value="">— skip —</option>
                        {fields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                            {f.required ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {row.status === "mapped" ? (
                        <span className="inline-flex rounded-full bg-[#ECFDF5] px-2 py-0.5 text-xs font-semibold text-[#065F46]">
                          Mapped
                          {row.required ? " *" : ""}
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-[#FEF3C7] px-2 py-0.5 text-xs font-semibold text-[#92400E]">
                          Not mapped
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setStep(1);
                setJobId("");
                setHeaders([]);
                setHeaderSamples({});
                setFields([]);
                setMapping({});
                setPreview(null);
                setProjectOverrides({});
                setAvailableSheets([]);
                setSelectedSheets([]);
              }}
            >
              Back
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={busy || !!unmappedRequired.length}
              onClick={handleSaveMapping}
            >
              {busy ? "Analyzing…" : "Preview unique / duplicate"}
            </YhButton>
          </div>
        </div>
      )}

      {step === 4 && preview && (
        <div className="space-y-4 rounded-xl border border-[#E5E7EB] bg-white p-4">
          <h2 className="text-base font-semibold">Preview</h2>
          {selectedSheets.length > 0 && (
            <p className="text-sm text-[#6B7280]">
              Sheets included:{" "}
              <strong className="text-[#111827]">
                {selectedSheets.join(", ")}
              </strong>
            </p>
          )}
          {(type === "leads" || type === "call_dataset") && (
            <div className="space-y-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
              <div>
                <h3 className="text-sm font-semibold text-[#111827]">
                  Project name mapping
                </h3>
                <p className="mt-0.5 text-xs text-[#6B7280]">
                  File values are matched to Projects by exact name (comma-separated
                  allowed). Unmatched names must be mapped manually before upload.
                </p>
              </div>
              {(preview?.projectResolution?.resolved || []).length ? (
                <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] px-3 py-2 text-sm text-[#065F46]">
                  Auto-mapped:{" "}
                  {(preview.projectResolution.resolved || [])
                    .slice(0, 12)
                    .map((r: any) => (
                      <span key={r.token} className="mr-2 inline-block">
                        <strong>{r.token}</strong>
                        {" → "}
                        {r.projectName || r.projectUid || r.projectId}
                      </span>
                    ))}
                </div>
              ) : null}
              {unresolvedProjects.length ? (
                <div className="space-y-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3">
                  <p className="text-sm font-semibold text-[#92400E]">
                    {unresolvedNeedsMap.length} project name(s) need manual
                    mapping
                  </p>
                  {unresolvedProjects.map((u: any) => {
                    const tok = String(u?.token || u || "").trim();
                    if (!tok) return null;
                    return (
                      <div
                        key={tok}
                        className="grid grid-cols-1 items-center gap-2 md:grid-cols-[1fr_1.2fr]"
                      >
                        <div className="text-sm text-[#78350F]">
                          <strong>{tok}</strong>
                          {u?.count ? (
                            <span className="ml-1 text-xs text-[#A16207]">
                              ({u.count} row{u.count === 1 ? "" : "s"})
                            </span>
                          ) : null}
                        </div>
                        <YhSelect
                          label=""
                          searchable
                          clearable
                          placeholder="Select project…"
                          searchPlaceholder="Search projects…"
                          options={projectOptions}
                          value={projectOverrides[tok] || ""}
                          onSearchChange={handleProjectSearch}
                          onChange={(e: any) =>
                            setProjectOverrides((prev) => ({
                              ...prev,
                              [tok]: String(e?.target?.value || ""),
                            }))
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] px-3 py-2 text-sm text-[#065F46]">
                  All project names in this file are mapped.
                </div>
              )}
            </div>
          )}
          {(subSource || sourceType) && (
            <p className="text-sm text-[#374151]">
              Source:{" "}
              <strong>
                {sourceType === "manual" ? "Manual" : "Bulk"}
              </strong>
              {" · "}
              Sub-source: <strong>{subSource || "—"}</strong>
              {" "}
              <span className="text-xs text-[#6B7280]">
                (applied to all rows)
              </span>
            </p>
          )}
          {type === "channel_partners" && (
            <>
              <p className="text-sm text-[#374151]">
                Phone masking for this batch:{" "}
                <strong>{phoneMaskingEnabled ? "Enabled" : "Disabled"}</strong>
              </p>
              <p className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-sm text-[#92400E]">
                Duplicates are by <strong>phone number</strong> (last 10 digits;
                leading 0 ignored), not by firm name. Two different firms with the
                same phone count as one unique CP + one duplicate row.
              </p>
            </>
          )}
          {type === "call_dataset" ? (
            <p className="rounded-lg border border-[#DBEAFE] bg-[#EFF6FF] px-3 py-2 text-sm text-[#1E3A8A]">
              Duplicate phone numbers are saved in the dataset. They stay here
              until you convert them, including numbers that already exist as leads.
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Total" value={preview.totals?.total} />
            <Stat
              label={type === "call_dataset" ? "New phones" : "Unique / new"}
              value={preview.totals?.unique}
            />
            <Stat
              label={type === "call_dataset" ? "Duplicate phones" : "Duplicate"}
              value={preview.totals?.duplicate}
            />
            {type === "call_dataset" ? (
              <Stat
                label="Will save (all)"
                value={
                  Number(preview.totals?.unique || 0) +
                  Number(preview.totals?.duplicate || 0)
                }
              />
            ) : (
              <Stat
                label="Membership update"
                value={preview.totals?.membershipUpdate}
              />
            )}
          </div>

          <SampleTable
            title="Unique sample"
            rows={preview.previewSample?.unique || []}
            preferKeys={[
              "lead_name",
              "lead_phone",
              "lead_email",
              "_projectInput",
              "_projectMapped",
              "_projectStatus",
            ]}
          />
          <SampleTable
            title="Duplicate sample"
            rows={preview.previewSample?.duplicate || []}
            preferKeys={[
              "lead_name",
              "lead_phone",
              "lead_email",
              "_projectInput",
              "_projectMapped",
              "_reason",
            ]}
          />
          {type === "channel_partners" && (
            <SampleTable
              title="Existing CP — membership update"
              rows={preview.previewSample?.membershipUpdate || []}
            />
          )}

          <div className="flex flex-wrap gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                setStep(3);
                setPreview(null);
              }}
            >
              Back
            </YhButton>
            {type === "call_dataset" ? (
              <>
                <YhButton
                  variant="primary"
                  size="sm"
                  disabled={busy || unresolvedNeedsMap.length > 0}
                  onClick={() => handleCommit("all")}
                >
                  Save all rows
                </YhButton>
                <YhButton
                  variant="secondary"
                  size="sm"
                  disabled={busy || unresolvedNeedsMap.length > 0}
                  onClick={() => handleCommit("unique_only")}
                >
                  Save unique phones only
                </YhButton>
              </>
            ) : (
              <YhButton
                variant="primary"
                size="sm"
                disabled={
                  busy ||
                  (type === "leads" && unresolvedNeedsMap.length > 0)
                }
                onClick={() =>
                  handleCommit(
                    type === "channel_partners"
                      ? "unique_and_membership_update"
                      : "unique_only"
                  )
                }
              >
                Save unique
                {type === "channel_partners" ? " + membership updates" : ""}
              </YhButton>
            )}
            <YhButton
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => handleCommit("discard")}
            >
              Discard
            </YhButton>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4 rounded-xl border border-[#E5E7EB] bg-white p-4">
          <h2 className="text-base font-semibold">Processing</h2>
          <div className="h-3 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
            <div
              className="h-full bg-[#059669] transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="text-sm text-[#374151]">
            {progress?.progress?.message || progress?.status || "…"} —{" "}
            {percent}%
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Created" value={progress?.totals?.success ?? 0} />
            <Stat
              label="Duplicates skipped"
              value={progress?.totals?.duplicate ?? 0}
            />
            <Stat label="Failed" value={progress?.totals?.fail ?? 0} />
            <Stat label="Total rows" value={progress?.totals?.total ?? 0} />
          </div>
          {Number(progress?.totals?.success || 0) > 0 ? (
            <p className="rounded-lg bg-[#ECFDF5] px-3 py-2 text-sm text-[#065F46]">
              {type === "call_dataset"
                ? `${progress?.totals?.success} row(s) were saved in this call dataset. They are not leads until you convert them.`
                : `${progress?.totals?.success} unique lead(s) were created and saved.`}
              {type !== "call_dataset" && Number(progress?.totals?.duplicate || 0) > 0
                ? ` ${progress.totals.duplicate} duplicate row(s) were skipped (not inserted).`
                : ""}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {Number(progress?.totals?.success || 0) > 0 &&
              ["completed", "partial", "failed"].includes(
                String(progress?.status || "")
              ) && (
                <>
                  <YhButton
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={async () => {
                      if (type === "call_dataset") {
                        const name = encodeURIComponent(
                          String(setName || progress?.setName || "").trim()
                        );
                        history.push(
                          `${urls.callDatasetsPath}${name ? `?dataset=${name}` : ""}`
                        );
                        return;
                      }
                      try {
                        setBusy(true);
                        await publishPartialBulkJob(jobId);
                        openSuccessNotification(
                          `${progress?.totals?.success || 0} saved lead(s) are ready in All Leads`
                        );
                        loadRecentJobs();
                        history.push(urls.ALLLEADFORMLIST);
                      } catch (e: any) {
                        openErrorNotification(
                          e?.message || "Could not open saved leads"
                        );
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {type === "call_dataset"
                      ? "Open call dataset"
                      : `Use saved leads (${progress?.totals?.success || 0})`}
                  </YhButton>
                  <YhButton
                    variant="secondary"
                    size="sm"
                    onClick={() => history.push(urls.leadsBulkUploadSetsPath)}
                  >
                    View upload sets
                  </YhButton>
                </>
              )}
            {(Number(progress?.totals?.fail || 0) > 0 ||
              Number(progress?.totals?.duplicate || 0) > 0) &&
              ["partial", "failed", "completed"].includes(
                String(progress?.status || "")
              ) && (
                <YhButton
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    downloadFailedReport(jobId).catch((e) =>
                      openErrorNotification(e.message)
                    )
                  }
                >
                  Download skipped / failed.xlsx
                </YhButton>
              )}
            {Number(progress?.totals?.fail || 0) > 0 &&
              ["partial", "failed"].includes(String(progress?.status || "")) && (
                <YhButton
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    retryBulkJob(jobId)
                      .then(() => {
                        openSuccessNotification("Retry started");
                        pollProgress(jobId);
                      })
                      .catch((e) => openErrorNotification(e.message))
                  }
                >
                  Retry failed
                </YhButton>
              )}
            {["completed", "partial", "failed"].includes(
              String(progress?.status || "")
            ) && (
              <YhButton
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStep(1);
                  setJobId("");
                  setPreview(null);
                  setProgress(null);
                  setFile(null);
                  setSetName("");
                }}
              >
                Create another
              </YhButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="rounded-lg bg-[#F9FAFB] p-3">
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className="text-lg font-semibold text-[#111827]">{value ?? 0}</div>
    </div>
  );
}

function SampleTable({
  title,
  rows,
  preferKeys = [],
}: {
  title: string;
  rows: any[];
  preferKeys?: string[];
}) {
  if (!rows?.length) return null;
  const labelFor = (k: string) => {
    if (k === "_projectInput") return "Project (file)";
    if (k === "_projectMapped") return "Project (matched)";
    if (k === "_projectStatus") return "Project status";
    if (k === "_reason") return "Reason";
    return k;
  };
  const allKeys = Object.keys(rows[0] || {});
  const preferred = preferKeys.filter((k) => allKeys.includes(k));
  const rest = allKeys.filter(
    (k) => !preferred.includes(k) && (!k.startsWith("_") || preferKeys.includes(k))
  );
  const keys = [...preferred, ...rest].slice(0, 8);
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-[#111827]">{title}</h3>
      <div className="overflow-auto rounded-lg border border-[#E5E7EB]">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-[#F9FAFB]">
            <tr>
              {keys.map((k) => (
                <th key={k} className="px-2 py-1 font-medium">
                  {labelFor(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r, i) => (
              <tr key={i} className="border-t border-[#F3F4F6]">
                {keys.map((k) => (
                  <td
                    key={k}
                    className={`px-2 py-1 ${
                      k === "_reason" || r._projectStatus === "needs_manual_map"
                        ? "text-[#B45309]"
                        : ""
                    }`}
                  >
                    {String(r[k] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BulkUploadWizard;
